/**
 * WelcomePage Component
 * First-time password setup page for new users invited by an admin.
 * Matches the ResetPasswordPage design with welcome-specific messaging.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { oathValidateResetToken, oathResetPassword } from '../../services/oathAuthService';
import { DualRingSpinner, SimpleSpinner } from '../ui/Spinner';

const WelcomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        setIsTokenValid(false);
        return;
      }

      const result = await oathValidateResetToken(token);
      setIsValidating(false);
      setIsTokenValid(result.valid === true);
    };

    validateToken();
  }, [token]);

  // Password complexity: at least 2 of 3 (uppercase, lowercase, number)
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const complexityCount = [hasUppercase, hasLowercase, hasNumber].filter(Boolean).length;

  // Password requirements check
  const passwordRequirements = [
    { label: 'At least 8 characters', met: newPassword.length >= 8 },
    { label: 'Includes at least 2 of: uppercase, lowercase, number', met: complexityCount >= 2 },
    { label: 'Passwords match', met: newPassword && confirmPassword && newPassword === confirmPassword },
  ];

  const allRequirementsMet = passwordRequirements.every((req) => req.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword) {
      setError('Please enter a password');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (complexityCount < 2) {
      setError('Password must include at least two of the following: uppercase letter, lowercase letter, number');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    const result = await oathResetPassword(token, newPassword, confirmPassword);

    setIsSubmitting(false);

    if (result.success) {
      setIsSuccess(true);
    } else {
      setError(result.error || 'Failed to set password');
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>

        {/* Header */}
        <div className="py-5 px-4 bg-white border-b border-gray-100 relative z-10">
          <div className="flex flex-col items-center gap-1">
            <img
              src="/bankers-surety-logo.png"
              alt="Bankers Surety"
              className="h-9"
            />
            <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
          <div className="text-center">
            <DualRingSpinner size="lg" className="mx-auto" />
            <p className="mt-4 text-sm text-primary-100">Validating your link...</p>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-5 px-4 text-center relative z-10">
          <p className="text-xs text-primary-200/80">
            &copy; {new Date().getFullYear()} Bankers Surety. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Invalid/expired token state
  if (!isTokenValid) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>

        {/* Header */}
        <div className="py-5 px-4 bg-white border-b border-gray-100 relative z-10">
          <div className="flex flex-col items-center gap-1">
            <img
              src="/bankers-surety-logo.png"
              alt="Bankers Surety"
              className="h-9"
            />
            <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
          <div className="w-full max-w-sm sm:max-w-md">
            {/* Error Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
              <div className="h-1 bg-gradient-to-r from-red-400 via-red-500 to-red-400"></div>
              <div className="p-6 sm:p-8 lg:p-10 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ExclamationCircleIcon className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Invalid or Expired Link
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  This setup link is invalid or has expired.
                  Setup links are valid for 72 hours after being sent.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Please contact your administrator to request a new invitation.
                </p>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full py-3 px-4 text-gray-600 hover:text-primary font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Back to Sign In
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-5 px-4 text-center relative z-10">
          <p className="text-xs text-primary-200/80">
            &copy; {new Date().getFullYear()} Bankers Surety. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>

        {/* Header */}
        <div className="py-5 px-4 bg-white border-b border-gray-100 relative z-10">
          <div className="flex flex-col items-center gap-1">
            <img
              src="/bankers-surety-logo.png"
              alt="Bankers Surety"
              className="h-9"
            />
            <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
          <div className="w-full max-w-sm sm:max-w-md">
            {/* Success Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
              <div className="h-1 bg-gradient-to-r from-green-400 via-green-500 to-green-400"></div>
              <div className="p-6 sm:p-8 lg:p-10 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Account Setup Complete
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  Your password has been set successfully.
                  You can now sign in to the Agency Portal.
                </p>
                <button
                  onClick={handleBackToLogin}
                  className="w-full py-3.5 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-5 px-4 text-center relative z-10">
          <p className="text-xs text-primary-200/80">
            &copy; {new Date().getFullYear()} Bankers Surety. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  // Password setup form
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
      </div>

      {/* Header */}
      <div className="py-5 px-4 bg-white border-b border-gray-100 relative z-10">
        <div className="flex flex-col items-center gap-1">
          <img
            src="/bankers-surety-logo.png"
            alt="Bankers Surety"
            className="h-9"
          />
          <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
        <div className="w-full max-w-sm sm:max-w-md">
          {/* Branding */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
              Welcome!
            </h1>
            <p className="text-sm sm:text-base text-primary-100 mt-2">
              Create your password to get started
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
            <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400"></div>
            <div className="p-6 sm:p-8 lg:p-10">
              {/* Error Alert */}
              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-700 text-center">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* New Password Field */}
                <div className="mb-4">
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      autoFocus
                      placeholder="Enter your password"
                      className="w-full pl-12 pr-12 py-3.5 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      disabled={isSubmitting}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 active:text-gray-800 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-all duration-200"
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm Password Field */}
                <div className="mb-4">
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      placeholder="Confirm your password"
                      className="w-full pl-12 pr-12 py-3.5 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      disabled={isSubmitting}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 active:text-gray-800 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-all duration-200"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="mb-6 space-y-1.5">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {req.met ? (
                        <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <XMarkIcon className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${req.met ? 'text-green-600' : 'text-gray-500'}`}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !allRequirementsMet}
                  className="w-full py-3.5 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5 mb-4"
                >
                  {isSubmitting ? (
                    <>
                      <SimpleSpinner size="sm" className="text-white mr-2" />
                      <span>Setting up...</span>
                    </>
                  ) : (
                    'Create Password'
                  )}
                </button>

                {/* Back to Login */}
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full py-3 px-4 text-gray-600 hover:text-primary font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Back to Sign In
                </button>
              </form>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="text-sm text-primary-100">Need assistance?</span>
              <a
                href="tel:877-778-5245"
                className="text-sm text-white font-semibold hover:text-primary-50 transition-colors"
              >
                877-778-5245
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-5 px-4 text-center relative z-10">
        <p className="text-xs text-primary-200/80">
          &copy; {new Date().getFullYear()} Bankers Surety. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default WelcomePage;
